import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useRazorpay from 'react-razorpay';
import { request } from '../services/api';
import SessionManager from '../utils/sessionManager';
import LandingHeader from '../components/organisms/LandingHeader';
import Footer from '../components/organisms/Footer';
import { CreditCard, Shield, CheckCircle, ArrowLeft, Wallet } from 'lucide-react';

const PaymentPage = () => {
  const navigate = useNavigate();
  const [Razorpay] = useRazorpay();
  
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const paymentType = urlParams.get('type') || 'credits'; // 'credits' or 'course'
  const courseId = urlParams.get('course_id');
  const courseAmount = parseFloat(urlParams.get('amount')) || 0;
  
  const [loading, setLoading] = useState(false);
  const [conversionRate, setConversionRate] = useState(1.0);
  const [credits, setCredits] = useState(100);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [recentOrders, setRecentOrders] = useState([]);
  
  // Billing details
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  // Get candidate info from session
  const session = SessionManager.getSession('candidate');
  const candidateId = session?.profile?.id;
  const userEmail = session?.profile?.email;

  useEffect(() => {
    // Check if user is logged in
    if (!candidateId) {
      navigate('/login');
      return;
    }
    
    fetchConversionRate();
    fetchCurrentBalance();
    fetchRecentOrders();
    loadUserDetails();
  }, [candidateId, navigate]);

  const loadUserDetails = () => {
    if (session?.profile) {
      const firstName = session.profile.first_name || '';
      const lastName = session.profile.last_name || '';
      const name = `${firstName} ${lastName}`.trim();
      const email = session.profile.email || '';
      
      setBillingDetails(prev => ({ ...prev, name, email }));
    }
  };

  const fetchConversionRate = async () => {
    try {
      console.log('🔍 [PAYMENT] Fetching conversion rate...');
      const data = await request('/payments/conversion-rate');
      console.log('✅ [PAYMENT] Conversion rate fetched:', data);
      setConversionRate(data.inr_to_credit_rate);
    } catch (error) {
      console.error('❌ [PAYMENT] Failed to fetch conversion rate:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const fetchCurrentBalance = async () => {
    try {
      console.log('🔍 [PAYMENT] Fetching current balance for candidate:', candidateId);
      const data = await request(`/credits/balance?candidate_id=${candidateId}`);
      console.log('✅ [PAYMENT] Balance fetched:', data.balance);
      setCurrentBalance(data.balance);
    } catch (error) {
      console.error('❌ [PAYMENT] Failed to fetch balance:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const fetchRecentOrders = async () => {
    try {
      console.log('🔍 [PAYMENT] Fetching recent orders for candidate:', candidateId);
      const data = await request(`/payments/credits/orders?candidate_id=${candidateId}&limit=5`);
      console.log('✅ [PAYMENT] Orders fetched:', data.length, 'orders');
      setRecentOrders(data);
    } catch (error) {
      console.error('❌ [PAYMENT] Failed to fetch orders:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const calculateAmount = () => {
    return (credits * conversionRate).toFixed(2);
  };

  const handlePurchase = async () => {
    console.log('🔍 [PAYMENT] Starting purchase process...');
    console.log('   Payment Type:', paymentType);
    console.log('   Candidate ID:', candidateId);
    
    if (paymentType === 'course') {
      // Course purchase
      console.log('   Course ID:', courseId);
      console.log('   Course Amount:', courseAmount);
      
      if (!courseId || !courseAmount) {
        console.error('❌ [PAYMENT] Missing course details');
        alert('Invalid course purchase details');
        return;
      }
      
      if (!billingDetails.name || !billingDetails.email || !billingDetails.phone) {
        console.error('❌ [PAYMENT] Missing required billing details');
        alert('Please fill in all required billing details');
        return;
      }
      
      try {
        setLoading(true);

        // Create order for course
        console.log('🔍 [PAYMENT] Creating course order...');
        const orderPayload = {
          candidate_id: candidateId,
          purchase_type: "course",
          course_id: courseId,
          course_amount: courseAmount
        };
        console.log('   Payload:', orderPayload);
        
        const orderData = await request('/payments/courses/create-order', {
          method: 'POST',
          body: JSON.stringify(orderPayload)
        });
        
        console.log('✅ [PAYMENT] Course order created successfully:', orderData);

        // Razorpay options for course
        const options = {
          key: orderData.key_id,
          amount: orderData.amount_paise,
          currency: orderData.currency,
          name: 'Aptirix Course',
          description: 'Course Enrollment',
          order_id: orderData.order_id,
          prefill: {
            name: billingDetails.name,
            email: billingDetails.email,
            contact: billingDetails.phone
          },
          theme: {
            color: '#3b82f6'
          },
          handler: async (response) => {
            console.log('🔍 [PAYMENT] Course payment completed, verifying...');
            
            try {
              const verifyPayload = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              };
              
              const data = await request('/payments/verify-payment', {
                method: 'POST',
                body: JSON.stringify(verifyPayload)
              });

              console.log('✅ [PAYMENT] Course payment verified successfully:', data);
              
              alert(data.message);
              // Redirect to course page or my courses
              navigate(`/candidate/courses`);
            } catch (error) {
              console.error('❌ [PAYMENT] Course payment verification failed:', error);
              alert('Payment verification failed. Please contact support.');
            }
          },
          modal: {
            ondismiss: () => {
              console.log('⚠️ [PAYMENT] Payment modal dismissed by user');
              setLoading(false);
            }
          }
        };

        console.log('🔍 [PAYMENT] Opening Razorpay modal for course...');
        const rzp = new Razorpay(options);
        
        rzp.on('payment.failed', (response) => {
          console.error('❌ [PAYMENT] Course payment failed:', response);
          alert('Payment failed. Please try again.');
          setLoading(false);
        });
        
        rzp.open();
        
      } catch (error) {
        console.error('❌ [PAYMENT] Failed to create course order:', error);
        alert(error.response?.data?.detail || 'Failed to create order');
        setLoading(false);
      }
      
    } else {
      // Credit purchase (existing logic)
      console.log('   Credits:', credits);
      console.log('   Billing Details:', billingDetails);
      
      if (!billingDetails.name || !billingDetails.email || !billingDetails.phone) {
        console.error('❌ [PAYMENT] Missing required billing details');
        alert('Please fill in all required billing details');
        return;
      }

      if (credits < 1 || credits > 10000) {
        console.error('❌ [PAYMENT] Invalid credit amount:', credits);
        alert('Credits must be between 1 and 10,000');
        return;
      }

      try {
        setLoading(true);

        // Create order
        console.log('🔍 [PAYMENT] Creating order...');
        const orderPayload = {
          candidate_id: candidateId,
          credits: credits
        };
        console.log('   Payload:', orderPayload);
        
        const orderData = await request('/payments/credits/create-order', {
          method: 'POST',
          body: JSON.stringify(orderPayload)
        });
        
        console.log('✅ [PAYMENT] Order created successfully:', orderData);
        console.log('   Order ID:', orderData.order_id);
        console.log('   Amount (INR):', orderData.amount_inr);
        console.log('   Amount (Paise):', orderData.amount_paise);
        console.log('   Key ID:', orderData.key_id);

        // Razorpay options
        const options = {
          key: orderData.key_id,
          amount: orderData.amount_paise,
          currency: orderData.currency,
          name: 'Aptirix Credits',
          description: `Purchase ${credits} credits`,
          order_id: orderData.order_id,
          prefill: {
            name: billingDetails.name,
            email: billingDetails.email,
            contact: billingDetails.phone
          },
          notes: {
            address: billingDetails.address,
            city: billingDetails.city,
            state: billingDetails.state,
            pincode: billingDetails.pincode
          },
          theme: {
            color: '#3b82f6'
          },
          handler: async (response) => {
            console.log('🔍 [PAYMENT] Payment completed, verifying...');
            console.log('   Razorpay Order ID:', response.razorpay_order_id);
            console.log('   Razorpay Payment ID:', response.razorpay_payment_id);
            console.log('   Razorpay Signature:', response.razorpay_signature);
            
            try {
              const verifyPayload = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              };
              console.log('   Verify Payload:', verifyPayload);
              
              const data = await request('/payments/verify-payment', {
                method: 'POST',
                body: JSON.stringify(verifyPayload)
              });

              console.log('✅ [PAYMENT] Payment verified successfully:', data);
              console.log('   Credits added:', data.credits_added);
              console.log('   New balance:', data.new_balance);
              
              alert(data.message);
              fetchCurrentBalance();
              fetchRecentOrders();
              setCredits(100);
            } catch (error) {
              console.error('❌ [PAYMENT] Payment verification failed:', error);
              console.error('Error details:', error.response?.data);
              alert('Payment verification failed. Please contact support.');
            }
          },
          modal: {
            ondismiss: () => {
              console.log('⚠️ [PAYMENT] Payment modal dismissed by user');
              setLoading(false);
            }
          }
        };

        console.log('🔍 [PAYMENT] Opening Razorpay modal with options:', options);
        const rzp = new Razorpay(options);
        
        rzp.on('payment.failed', (response) => {
          console.error('❌ [PAYMENT] Payment failed:', response);
          console.error('   Error code:', response.error.code);
          console.error('   Error description:', response.error.description);
          console.error('   Error reason:', response.error.reason);
          alert('Payment failed. Please try again.');
          setLoading(false);
        });
        
        rzp.open();
        console.log('✅ [PAYMENT] Razorpay modal opened');
        
      } catch (error) {
        console.error('❌ [PAYMENT] Failed to create order:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        alert(error.response?.data?.detail || 'Failed to create order');
        setLoading(false);
      }
    }
  };

  const quickAmounts = [20, 50, 100, 250, 500, 1000];

  return (
    <div className="min-h-screen bg-gray-50">
      <LandingHeader />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
          <div className="bg-[#0a1929] rounded-2xl p-8 text-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6" />
              </div>
              <h1 className="text-3xl font-bold">
                {paymentType === 'course' ? 'Course Enrollment Payment' : 'Purchase Credits'}
              </h1>
            </div>
            {paymentType === 'course' ? (
              <p className="text-gray-300 text-lg">
                Complete payment to enroll in the course
              </p>
            ) : (
              <div className="flex items-center gap-2 text-lg">
                <span className="text-gray-300">Current Balance:</span>
                <span className="font-bold text-blue-400">{currentBalance} Credits</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Side - Billing Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Billing Information */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                Billing Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={billingDetails.name}
                    onChange={(e) => setBillingDetails({ ...billingDetails, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={billingDetails.email}
                    onChange={(e) => setBillingDetails({ ...billingDetails, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={billingDetails.phone}
                    onChange={(e) => setBillingDetails({ ...billingDetails, phone: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="9999999999"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Pincode
                  </label>
                  <input
                    type="text"
                    value={billingDetails.pincode}
                    onChange={(e) => setBillingDetails({ ...billingDetails, pincode: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="400001"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={billingDetails.address}
                    onChange={(e) => setBillingDetails({ ...billingDetails, address: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Street address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={billingDetails.city}
                    onChange={(e) => setBillingDetails({ ...billingDetails, city: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Mumbai"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={billingDetails.state}
                    onChange={(e) => setBillingDetails({ ...billingDetails, state: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Maharashtra"
                  />
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                Recent Orders
              </h2>
              
              {recentOrders.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Wallet className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">No orders yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-4 px-4 text-sm font-bold text-gray-700">Date</th>
                        <th className="text-left py-4 px-4 text-sm font-bold text-gray-700">Credits</th>
                        <th className="text-left py-4 px-4 text-sm font-bold text-gray-700">Amount</th>
                        <th className="text-left py-4 px-4 text-sm font-bold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order) => (
                        <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-4 px-4 text-sm text-gray-600">
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-900 font-semibold">
                            {order.credits_to_purchase}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-900 font-semibold">
                            ₹{order.amount_inr}
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                              order.status === 'paid' ? 'bg-green-100 text-green-700' :
                              order.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8 sticky top-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
              
              {paymentType === 'course' ? (
                /* Course Purchase Summary */
                <>
                  <div className="mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-4">
                      <p className="text-sm text-gray-600 mb-2 font-medium">Course Enrollment</p>
                      <p className="text-xl font-bold text-gray-900">Course ID: {courseId}</p>
                    </div>
                  </div>

                  {/* Pricing Details */}
                  <div className="bg-gray-50 rounded-xl p-6 mb-6 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Course Fee</span>
                      <span className="font-bold text-gray-900">₹{courseAmount}</span>
                    </div>
                    <div className="border-t-2 border-gray-200 pt-4 flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total Amount</span>
                      <span className="text-3xl font-bold text-blue-600">
                        ₹{courseAmount}
                      </span>
                    </div>
                  </div>

                  {/* Purchase Button */}
                  <button
                    onClick={handlePurchase}
                    disabled={loading}
                    className="w-full bg-[#0a1929] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#0d2137] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                  >
                    {loading ? 'Processing...' : 'Proceed to Payment'}
                  </button>
                </>
              ) : (
                /* Credit Purchase Summary */
                <>
                  {/* Credit Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-bold text-gray-700 mb-4">
                      Select Credits
                    </label>
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      {quickAmounts.map((amount) => (
                        <button
                          key={amount}
                          onClick={() => setCredits(amount)}
                          className={`py-3 px-4 rounded-xl border-2 font-bold transition-all ${
                            credits === amount
                              ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-md'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                        >
                          {amount}
                        </button>
                      ))}
                    </div>
                    
                    <div className="relative">
                      <input
                        type="number"
                        value={credits}
                        onChange={(e) => setCredits(parseInt(e.target.value) || 0)}
                        min="20"
                        max="10000"
                        className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-bold transition-all"
                        placeholder="Enter credits"
                      />
                      <span className="absolute right-4 top-4 text-gray-500 font-semibold">Credits</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-3 font-medium">Min: 20 | Max: 10,000</p>
                  </div>

                  {/* Pricing Details */}
                  <div className="bg-gray-50 rounded-xl p-6 mb-6 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Credits</span>
                      <span className="font-bold text-gray-900">{credits}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Rate per Credit</span>
                      <span className="font-bold text-gray-900">₹{conversionRate}</span>
                    </div>
                    <div className="border-t-2 border-gray-200 pt-4 flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total Amount</span>
                      <span className="text-3xl font-bold text-blue-600">
                        ₹{calculateAmount()}
                      </span>
                    </div>
                  </div>

                  {/* Purchase Button */}
                  <button
                    onClick={handlePurchase}
                    disabled={loading || credits < 20}
                    className="w-full bg-[#0a1929] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#0d2137] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                  >
                    {loading ? 'Processing...' : 'Proceed to Payment'}
                  </button>
                </>
              )}

              {/* Security Badge */}
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-600 bg-green-50 py-3 rounded-xl border border-green-200">
                <Shield className="w-5 h-5 text-green-600" />
                <span className="font-semibold">Secure Payment via Razorpay</span>
              </div>

              {/* Payment Methods */}
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500 mb-3 font-semibold">We accept</p>
                <div className="flex justify-center gap-2 flex-wrap">
                  <span className="text-xs bg-gray-100 px-4 py-2 rounded-lg font-semibold text-gray-700 border border-gray-200">Cards</span>
                  <span className="text-xs bg-gray-100 px-4 py-2 rounded-lg font-semibold text-gray-700 border border-gray-200">UPI</span>
                  <span className="text-xs bg-gray-100 px-4 py-2 rounded-lg font-semibold text-gray-700 border border-gray-200">Net Banking</span>
                  <span className="text-xs bg-gray-100 px-4 py-2 rounded-lg font-semibold text-gray-700 border border-gray-200">Wallets</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PaymentPage;
